/**
 * WHO IS ON IT (Corbin, 10/16) — the people panel in the task drawer.
 *
 *   - Take it   moves a teammate's task to you. They and the asker see a line
 *               in Recent activity and in "What happened". No bell, no text.
 *   - Help      adds you; the person holding it keeps it.
 *   - Add a person / Remove me   on a shared task.
 *
 * On a shared task it shows each person's state (a check, or a coloured
 * dot) and, for "everyone must finish", how many parts are in.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { firstName, HOLDER_STATE_WORDS, type HolderState, type Task, type TaskHolder } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";

const STATE_TONE: Record<HolderState, string> = {
    done: "text-emerald-700 dark:text-emerald-400",
    working: "text-sky-700 dark:text-sky-400",
    waiting: "text-violet-700 dark:text-violet-400",
    opened: "text-amber-800 dark:text-amber-300",
    not_opened: "text-red-700 dark:text-red-400",
};

export function TaskPeople({
    task,
    holders,
    me,
    isOffice,
    people,
    onChanged,
}: {
    task: Task;
    holders: TaskHolder[];
    me: string | undefined;
    isOffice: boolean;
    /** Everyone who could be added (active people, not me). */
    people: Array<{ id: string; first_name: string; last_name: string }>;
    onChanged: () => void;
}) {
    const [adding, setAdding] = useState("");
    const act = useMutation({
        mutationFn: (a: { kind: "take" } | { kind: "help" } | { kind: "leave" } | { kind: "add"; userId: string }) =>
            a.kind === "take"
                ? api.post(`/api/tasks/${task.id}/take`)
                : a.kind === "help"
                  ? api.post(`/api/tasks/${task.id}/help`)
                  : a.kind === "leave"
                    ? api.post(`/api/tasks/${task.id}/leave`)
                    : api.post(`/api/tasks/${task.id}/help`, { userId: a.userId }),
        onSuccess: (_d, a) => {
            const holder = firstName(task.assigneeName);
            const added = a.kind === "add" ? people.find((p) => p.id === a.userId)?.first_name : null;
            toast.success(
                a.kind === "take"
                    ? `It's yours now. ${holder} will see you took it.`
                    : a.kind === "help"
                      ? `You're on it with ${holder}.`
                      : a.kind === "leave"
                        ? "You're off this one."
                        : `${added ?? "They"} can see it now.`,
            );
            setAdding("");
            onChanged();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "That didn't work. Try again."),
    });

    if (task.status !== "open") return null;
    const shared = holders.length > 1;
    const iHold = task.assigneeUserId === me;
    const iHelp = !iHold && holders.some((h) => h.userId === me);
    const canTake = isOffice && !!task.assigneeUserId && !iHold && !task.whatsNewNoteId;
    const canAdd = isOffice && !!task.assigneeUserId && !task.whatsNewNoteId;
    const onIt = new Set([task.assigneeUserId, ...holders.map((h) => h.userId)]);
    const addable = people.filter((p) => !onIt.has(p.id));
    if (!shared && !canTake && !canAdd) return null;
    const done = holders.filter((h) => h.state === "done").length;

    return (
        <div className="space-y-2 rounded-lg border p-3" data-testid="task-people">
            {shared && (
                <>
                    <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-semibold">Who is on it</span>
                        <span className="text-xs font-semibold text-muted-foreground">
                            {task.doneRule === "everyone"
                                ? `${done} of ${holders.length} done · everyone must finish`
                                : "Done when anyone finishes"}
                        </span>
                    </div>
                    {task.doneRule === "everyone" && (
                        <div
                            className="h-2 overflow-hidden rounded-full bg-secondary"
                            role="img"
                            aria-label={`${done} of ${holders.length} done`}
                        >
                            <div
                                className="h-full bg-emerald-600 dark:bg-emerald-400"
                                style={{ width: `${Math.round((done / holders.length) * 100)}%` }}
                            />
                        </div>
                    )}
                    <ul className="space-y-1.5">
                        {holders.map((h) => (
                            <li key={h.userId} className="flex items-center gap-2 rounded-md border px-2.5 py-1.5">
                                <span
                                    className={cn(
                                        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold",
                                        h.state === "done"
                                            ? "bg-emerald-600 text-white dark:bg-emerald-500"
                                            : "border-2 border-border",
                                    )}
                                    aria-hidden
                                >
                                    {h.state === "done" ? "✓" : ""}
                                </span>
                                <span className="min-w-0 truncate text-sm font-medium">
                                    {h.userId === me ? "You" : firstName(h.name)}
                                    {h.isLead && <span className="text-xs text-muted-foreground"> · holds it</span>}
                                </span>
                                <span className={cn("ml-auto shrink-0 text-xs font-semibold", STATE_TONE[h.state])}>
                                    {h.state === "done" && h.partNote
                                        ? `Done · “${h.partNote}”`
                                        : HOLDER_STATE_WORDS[h.state]}
                                </span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
                {canTake && (
                    <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ kind: "take" })}>
                        Take it
                    </Button>
                )}
                {canTake && !iHelp && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={act.isPending}
                        onClick={() => act.mutate({ kind: "help" })}
                    >
                        Help
                    </Button>
                )}
                {iHelp && (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={act.isPending}
                        onClick={() => act.mutate({ kind: "leave" })}
                    >
                        Remove me
                    </Button>
                )}
                {canAdd && addable.length > 0 && (
                    <Select
                        value={adding}
                        aria-label="Add a person"
                        className="h-9 min-w-[160px] flex-1 text-sm"
                        onChange={(e) => {
                            const id = e.target.value;
                            setAdding(id);
                            if (id) act.mutate({ kind: "add", userId: id });
                        }}
                    >
                        <option value="">+ Add a person</option>
                        {addable.map((p) => (
                            <option key={p.id} value={p.id}>
                                {p.first_name} {p.last_name}
                            </option>
                        ))}
                    </Select>
                )}
            </div>
        </div>
    );
}
